import { extent, tickStep } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import type PptxGenJS from 'pptxgenjs';

import { FormatType, getFormatter, ReplotChartNames } from '@revelio/replots';

import clientLogos from '../../../../../../../images/client-logos.svg';
import {
  type BoxDataPoint,
  type BreakdownBoxDataSet,
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
} from '../../../../DataProvider/types';
import type { MetricValues } from '../../../../PageConfigProvider/types/metric';
import type { ColumnMetaMap } from '../../../Table/utils/convertSeriesToTableData';
import { getFormat } from '../../../utils/getFormat';
import { type CapturedChartImage, fitImageToBox } from './ppt-chart-image';
import { buildSheetData, generateChartData } from './ppt-data-processor';
import {
  getActiveBrandConfig,
  getChartOptions,
  PptxChartType,
  pptTheme,
  reportPptTheme,
} from './ppt-theme';

interface PptChartSeries {
  name: string;
  labels: string[];
  values: number[];
}

interface VolumeChartData {
  lineData: PptChartSeries[];
  barData: PptChartSeries[];
}

interface MixedStatsChart {
  name: string;
  labels: (string | number)[];
  values: number[];
  lastValue: number;
  deltaPercentage: number;
}

function isVolumeChartData(data: unknown): data is VolumeChartData {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const obj = data as Record<string, unknown>;
  return (
    'lineData' in obj &&
    'barData' in obj &&
    Array.isArray(obj.lineData) &&
    Array.isArray(obj.barData)
  );
}

function isChartSeriesArray(data: unknown): data is PptChartSeries[] {
  return Array.isArray(data);
}

function normalizeLabels(labels: (string | number)[]): string[] {
  return labels.map((label) => String(label));
}

function toPptxHex(color: string): string {
  return typeof color === 'string' ? color.replace('#', '') : '';
}

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));

/** Default gauge gradient (blue→red), the customize panel's default gauge palette. */
const GAUGE_DEFAULT_COLORS = ['55A3FF', 'FF646D'];

const hexToRgb = (hex: string): [number, number, number] => {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean.padStart(6, '0').slice(0, 6);
  const num = Number.parseInt(full, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
};

const channelToHex = (value: number): string =>
  Math.round(Math.max(0, Math.min(255, value)))
    .toString(16)
    .padStart(2, '0');

const lightenHexColor = (hex: string, amount: number): string => {
  const [r, g, b] = hexToRgb(hex);
  return [
    channelToHex(r + (255 - r) * amount),
    channelToHex(g + (255 - g) * amount),
    channelToHex(b + (255 - b) * amount),
  ].join('');
};

/** Linearly interpolate across evenly-spaced color stops, returning a `#`-less hex. */
const interpolateGaugeColor = (colors: string[], t: number): string => {
  const stops = colors.length >= 2 ? colors : GAUGE_DEFAULT_COLORS;
  const scaled = clamp01(t) * (stops.length - 1);
  const lowerIndex = Math.min(stops.length - 2, Math.floor(scaled));
  const frac = scaled - lowerIndex;
  const [r1, g1, b1] = hexToRgb(stops[lowerIndex] ?? '55A3FF');
  const [r2, g2, b2] = hexToRgb(stops[lowerIndex + 1] ?? 'FF646D');
  return [
    channelToHex(r1 + (r2 - r1) * frac),
    channelToHex(g1 + (g2 - g1) * frac),
    channelToHex(b1 + (b2 - b1) * frac),
  ].join('');
};

/**
 * A gauge is only exportable when it has a finite numeric value or a non-empty
 * primary text. This keeps PPT output in sync with the live chart, which
 * renders `ChartError` (and nothing exportable) when it has no data.
 */
export const hasExportableGaugeContent = (gauge: {
  mainText?: string;
  value?: number;
}): boolean => {
  const hasValue =
    typeof gauge.value === 'number' && Number.isFinite(gauge.value);
  const hasMainText = !!gauge.mainText?.trim();
  return hasValue || hasMainText;
};

const drawGaugeV2Track = (
  slide: PptxGenJS.Slide,
  {
    barX,
    barW,
    barY,
    barH,
    ratio,
    palette,
    valueText,
    valueFontSize,
  }: {
    barX: number;
    barW: number;
    barY: number;
    barH: number;
    ratio: number;
    palette: string[];
    valueText: string;
    valueFontSize: number;
  }
): { markerH: number } => {
  const segments = 24;
  const segW = barW / segments;
  for (let i = 0; i < segments; i++) {
    slide.addShape('rect', {
      x: barX + i * segW,
      y: barY,
      w: segW + 0.01,
      h: barH,
      fill: {
        color: interpolateGaugeColor(palette, (i + 0.5) / segments),
      },
      line: { color: interpolateGaugeColor(palette, (i + 0.5) / segments) },
    });
  }

  const markerX = barX + ratio * barW;
  const markerH = barH * 2.4;
  slide.addShape('rect', {
    x: markerX - 0.015,
    y: barY + barH / 2 - markerH / 2,
    w: 0.03,
    h: markerH,
    fill: { color: pptTheme.colors.text },
    line: { color: pptTheme.colors.text },
  });

  if (valueText) {
    const valueBoxW = 0.7;
    slide.addText(valueText, {
      x: markerX - valueBoxW / 2,
      y: barY + markerH * 0.55 + 0.06,
      w: valueBoxW,
      h: 0.22,
      fontSize: valueFontSize,
      bold: true,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'center',
      valign: 'top',
      wrap: false,
    });
  }

  return { markerH };
};

const GAUGE_V2_CONTEXT_BLOCK_H = 0.42;
const GAUGE_V2_ENTITY_LABEL_H = 0.2;

const renderGaugeContent = (
  slide: PptxGenJS.Slide,
  opts: ReportSlotLayout & {
    mainText?: string;
    minLabel?: string;
    maxLabel?: string;
    value?: number;
    entities?: { label?: string; value: number }[];
    colors?: string[];
    subFontSize: number;
  }
) => {
  const {
    x,
    y,
    w,
    h,
    mainText,
    minLabel,
    maxLabel,
    value,
    entities,
    colors,
    subFontSize,
  } = opts;

  const palette =
    colors && colors.length >= 2 ? colors.map(toPptxHex) : GAUGE_DEFAULT_COLORS;
  const series = entities?.length
    ? entities
    : [
        {
          value:
            typeof value === 'number' && Number.isFinite(value) ? value : 0.5,
        },
      ];
  const stacked = series.length > 1;

  const padX = w * 0.06;
  const barX = x + padX;
  const barW = w - padX * 2;
  const valueFontSize = reportPptTheme.fonts.gaugeV2ValueTextSize;
  const rowH = stacked ? (h - GAUGE_V2_CONTEXT_BLOCK_H) / series.length : h;

  let lastTrackBottom = y;

  series.forEach((entity, index) => {
    const ratio = clamp01(entity.value);
    const rowY = y + index * rowH;
    const barH = stacked
      ? Math.min(0.1, rowH * 0.16)
      : Math.min(0.12, h * 0.12);
    const barY = stacked
      ? rowY + GAUGE_V2_ENTITY_LABEL_H + 0.04
      : rowY + h * 0.35;

    if (stacked) {
      if (entity.label?.trim()) {
        slide.addText(entity.label.trim(), {
          x: barX,
          y: rowY,
          w: barW,
          h: GAUGE_V2_ENTITY_LABEL_H,
          fontSize: subFontSize,
          color: pptTheme.colors.text,
          fontFace: pptTheme.fonts.face,
          align: 'left',
          valign: 'bottom',
        });
      }
    } else {
      const rangeLabelH = 0.28;
      slide.addText('0%', {
        x: barX,
        y: barY - rangeLabelH - 0.04,
        w: barW * 0.25,
        h: rangeLabelH,
        fontSize: subFontSize,
        color: pptTheme.colors.text,
        fontFace: pptTheme.fonts.face,
        align: 'left',
        valign: 'bottom',
      });
      slide.addText('100%', {
        x: barX + barW * 0.75,
        y: barY - rangeLabelH - 0.04,
        w: barW * 0.25,
        h: rangeLabelH,
        fontSize: subFontSize,
        color: pptTheme.colors.text,
        fontFace: pptTheme.fonts.face,
        align: 'right',
        valign: 'bottom',
      });
    }

    const { markerH } = drawGaugeV2Track(slide, {
      barX,
      barW,
      barY,
      barH,
      ratio,
      palette,
      valueText: stacked
        ? `${Math.round(ratio * 100)}%`
        : mainText?.trim() || `${Math.round(ratio * 100)}%`,
      valueFontSize,
    });

    lastTrackBottom = barY + markerH * 0.55;
  });

  const contextY = stacked
    ? y + h - GAUGE_V2_CONTEXT_BLOCK_H
    : lastTrackBottom + 0.42;
  const contextH = Math.max(0.3, h - (contextY - y));
  if (minLabel?.trim()) {
    slide.addText(minLabel.trim(), {
      x: barX,
      y: contextY,
      w: barW * 0.4,
      h: contextH,
      fontSize: subFontSize,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'left',
      valign: 'top',
    });
  }
  if (maxLabel?.trim()) {
    slide.addText(maxLabel.trim(), {
      x: barX + barW * 0.6,
      y: contextY,
      w: barW * 0.4,
      h: contextH,
      fontSize: subFontSize,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'right',
      valign: 'top',
    });
  }
};

function isBreakdownSingleTwoEntity(
  rawData: unknown
): rawData is BreakdownSingleDataSet & {
  data: [
    BreakdownSingleDataSet['data'][number],
    BreakdownSingleDataSet['data'][number],
  ];
} {
  if (typeof rawData !== 'object' || rawData === null) {
    return false;
  }
  const ds = rawData as BreakdownSingleDataSet;
  if (ds.type !== DataType.BREAKDOWN_SINGLE) {
    return false;
  }
  if (!Array.isArray(ds.data) || ds.data.length !== 2) {
    return false;
  }
  return true;
}

/** True when any point in the two-entity dataset actually carries a share. */
function breakdownHasShares(entities: {
  data: { breakdown: SingleDataPoint[] }[];
}): boolean {
  return entities.data.some((entity) =>
    entity.breakdown.some((point) => point.share != null)
  );
}

function getBreakdownMetric(
  entity: { breakdown: SingleDataPoint[] },
  dimension: string,
  useShare: boolean
): number {
  const point = entity.breakdown.find((b) => b.dim === dimension);
  // Share-populated datasets keep the historical share view; the transitions
  // Role/Geography combos put raw people counts in `value` and leave `share`
  // unset, which the old share-only read exported as all-zero bars.
  return (useShare ? point?.share : point?.value) ?? 0;
}

/**
 * '#%' for fraction-scale data, plain thousands for counts — same magnitude
 * heuristic as `formatLatest` in the mixed-stats slide.
 */
function getDivergingAxisFormat(values: number[]): string {
  const maxAbs = Math.max(0, ...values.map((v) => Math.abs(v)));
  return maxAbs <= 2 ? '#%' : '#,##0';
}

/**
 * Derive the data-label number format from the plotted values: a deck has no
 * hover tooltips, so bar charts bake the numbers in — and the same chart type
 * carries raw counts (Headcount) and fraction rates/shares (Growth Rate,
 * composition segments). Stacked segments get whole percents; space inside a
 * segment is tight.
 */
const isFractionScale = (chartData: { values?: (number | null)[] }[]) => {
  const values = chartData.flatMap((series) => series.values ?? []);
  return Math.max(0, ...values.map((v) => Math.abs(v ?? 0))) <= 2;
};

function applyDataLabelFormat(
  options: PptxGenJS.IChartOpts,
  chartData: { values?: (number | null)[] }[]
): void {
  if (!options.showValue || options.dataLabelFormatCode) return;
  if (!isFractionScale(chartData)) {
    options.dataLabelFormatCode = '#,##0';
    return;
  }
  const isStacked =
    options.barGrouping === 'percentStacked' ||
    options.barGrouping === 'stacked';
  options.dataLabelFormatCode = isStacked ? '0%' : '0.0%';
}

export const chartTitleWithSelection = (
  title: string,
  selectionLabel?: string
): string => {
  const selection = selectionLabel?.trim();
  const base = title?.trim() ?? '';
  if (!selection) return base;
  if (!base) return selection;
  return base.includes(selection) ? base : `${base} (${selection})`;
};

const MAX_SELECTION_CHARS = 40;
/** Room kept for the segment picks however long the entity name is. */
const MIN_SEGMENT_LIST_CHARS = 16;
/** A first pick cut shorter than this says too little to be worth showing. */
const MIN_FIRST_PICK_CHARS = 6;
const ENTITY_SEPARATOR = ': ';

const hiddenPicksSuffix = (count: number): string =>
  count === 0 ? '' : ` + ${count} ${count === 1 ? 'other' : 'others'}`;

/**
 * A dropdown's picks for an export title, within maxChars including the count
 * of the picks left out: "North America, Northern Europe + 1 other". Picks
 * are listed in order while they fit; the first is truncated to make room.
 * When even that leaves too little, the picks are counted the way the
 * dropdown counts them: "3 selected".
 */
export const formatSelectionList = (
  names: string[],
  maxChars: number = MAX_SELECTION_CHARS
): string => {
  const picks = names.map((name) => name.trim()).filter(Boolean);
  const [first, ...rest] = picks;
  if (!first) return '';

  const firstPickRoom = maxChars - hiddenPicksSuffix(rest.length).length;
  if (firstPickRoom < MIN_FIRST_PICK_CHARS) return `${picks.length} selected`;

  let label = truncateLabel(first, firstPickRoom);
  let shownCount = 1;
  for (const name of rest) {
    const next = `${label}, ${name}`;
    const suffix = hiddenPicksSuffix(picks.length - shownCount - 1);
    if (next.length + suffix.length > maxChars) break;
    label = next;
    shownCount += 1;
  }
  return `${label}${hiddenPicksSuffix(picks.length - shownCount)}`;
};

/**
 * The text export titles carry for a chart's dropdown, within
 * MAX_SELECTION_CHARS: its entity pick, its segment picks, or
 * "Entity: segments" when it holds both. With both, the entity is truncated
 * to leave the segments MIN_SEGMENT_LIST_CHARS.
 */
export const buildSelectionLabel = ({
  entityName,
  segmentNames,
}: {
  entityName?: string;
  segmentNames: string[];
}): string | undefined => {
  const entity = entityName?.trim();
  if (!entity) return formatSelectionList(segmentNames) || undefined;
  if (!segmentNames.some((name) => name.trim())) {
    return truncateLabel(entity, MAX_SELECTION_CHARS);
  }

  const prefix = `${truncateLabel(
    entity,
    MAX_SELECTION_CHARS - MIN_SEGMENT_LIST_CHARS - ENTITY_SEPARATOR.length
  )}${ENTITY_SEPARATOR}`;
  return `${prefix}${formatSelectionList(
    segmentNames,
    MAX_SELECTION_CHARS - prefix.length
  )}`;
};

const TITLE_SLIDE_DETAILS = {
  x: 0.5,
  y: 2.7,
  w: 3.9,
  h: 2.6,
  labelFontSize: 10,
  valueFontSize: 10,
  maxEntries: 6,
  maxValueChars: 60,
} as const;

export const addTitleSlideDetails = (
  slide: PptxGenJS.Slide,
  parameters: { label: string; value: string }[]
) => {
  const entries = parameters
    .filter((p) => p.label.trim() && p.value.trim())
    .slice(0, TITLE_SLIDE_DETAILS.maxEntries);
  if (entries.length === 0) return;

  const brand = getActiveBrandConfig();
  const runs: PptxGenJS.TextProps[] = entries.flatMap((entry, index) => [
    {
      text: entry.label,
      options: {
        bold: true,
        color: brand.coverTitleColor.replace('#', ''),
        fontSize: TITLE_SLIDE_DETAILS.labelFontSize,
        breakLine: true,
      },
    },
    {
      text: truncateLabel(entry.value, TITLE_SLIDE_DETAILS.maxValueChars),
      options: {
        color: brand.coverDescriptionColor.replace('#', ''),
        fontSize: TITLE_SLIDE_DETAILS.valueFontSize,
        breakLine: index < entries.length - 1,
      },
    },
  ]);

  slide.addText(runs, {
    x: TITLE_SLIDE_DETAILS.x,
    y: TITLE_SLIDE_DETAILS.y,
    w: TITLE_SLIDE_DETAILS.w,
    h: TITLE_SLIDE_DETAILS.h,
    fontFace: pptTheme.fonts.face,
    valign: 'top',
  });
};

export const addDivergingBarComboSlide = (
  slide: PptxGenJS.Slide,
  title: string,
  exportTitle: string | undefined,
  rawData: unknown,
  dataType?: string,
  selectedSegments?: { name: string }[]
) => {
  const slideTitle = exportTitle || '';

  if (!isBreakdownSingleTwoEntity(rawData)) {
    const { sheetData } = buildSheetData(
      rawData,
      dataType,
      ReplotChartNames.DivergingBarChart
    );
    if (!sheetData || sheetData.length === 0) {
      console.warn('No sheet data for diverging bar combo fallback');
      return;
    }
    const chartData = generateChartData(
      sheetData,
      ReplotChartNames.DivergingBarChart
    );
    const pptChart = {
      type: PptxChartType.bar as PptxGenJS.CHART_NAME,
      opts: {
        barDir: 'bar',
        barGrouping: 'stacked',
        showLegend: false,
        axisLabelFormatCode: '#%',
        catAxisLabelPos: 'low',
        valueBarColors: true,
        chartColors: [
          pptTheme.colors.divergingPositive.replace('#', ''),
          pptTheme.colors.divergingNegative.replace('#', ''),
        ],
        catAxisLabelFontSize: 10,
      },
    };
    addSlideContent(
      slide,
      title,
      exportTitle,
      chartData,
      pptChart,
      ReplotChartNames.DivergingBarChart,
      selectedSegments
    );
    return;
  }

  slide.addText(slideTitle || '', {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: pptTheme.layout.header.w,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const focusEntity = rawData.data[0];
  const baselineEntity = rawData.data[1];
  const useShare = breakdownHasShares(rawData);

  const dimensionSet = new Set<string>();
  for (const entity of rawData.data) {
    for (const point of entity.breakdown) {
      dimensionSet.add(point.dim);
    }
  }

  const dimensions = Array.from(dimensionSet);
  const rows = dimensions.map((dim) => {
    const baselineValue = getBreakdownMetric(baselineEntity, dim, useShare);
    const focusValue = getBreakdownMetric(focusEntity, dim, useShare);
    return {
      dim,
      leftValue: -baselineValue,
      rightValue: focusValue,
    };
  });
  rows.sort((a, b) => {
    const absA = Math.abs(getBreakdownMetric(baselineEntity, a.dim, useShare));
    const absB = Math.abs(getBreakdownMetric(baselineEntity, b.dim, useShare));
    if (absA !== absB) {
      return absA - absB;
    }
    return a.dim.localeCompare(b.dim);
  });

  const sortedLabels = rows.map((r) => r.dim);
  const leftValues = rows.map((r) => r.leftValue);
  const rightValues = rows.map((r) => r.rightValue);

  const tornadoSeries = [
    {
      name: baselineEntity.dim,
      labels: sortedLabels,
      values: leftValues,
    },
    {
      name: focusEntity.dim,
      labels: sortedLabels,
      values: rightValues,
    },
  ];

  const differenceValues = sortedLabels.map((dim) => {
    const leftValue = getBreakdownMetric(baselineEntity, dim, useShare);
    const rightValue = getBreakdownMetric(focusEntity, dim, useShare);
    return rightValue - leftValue;
  });

  /** Two series (negative vs positive), matching `getDivergingBarConfig` for SingleDataSet difference view. */
  const differenceSeries = [
    {
      name: 'Negative',
      labels: sortedLabels,
      values: differenceValues.map((d) => (d < 0 ? d : 0)),
    },
    {
      name: 'Positive',
      labels: sortedLabels,
      values: differenceValues.map((d) => (d > 0 ? d : 0)),
    },
  ];

  const valueFormatCode = getDivergingAxisFormat([
    ...leftValues,
    ...rightValues,
    ...differenceValues,
  ]);
  // pos;neg;zero format sections. The tornado's left side is negated only to
  // mirror the bars, so its labels drop the sign; the empty zero section hides
  // labels on zero points — the unused half of a mutually-exclusive pair.
  const unsignedValueFormatCode = `${valueFormatCode};${valueFormatCode};`;
  const signedValueFormatCode = `${valueFormatCode};-${valueFormatCode};`;

  const halfWidth = pptTheme.layout.chartWidth / 2 - 0.15;
  const leftX = pptTheme.layout.slidePaddingX;
  const rightX = pptTheme.layout.slidePaddingX + halfWidth + 0.3;

  slide.addChart(PptxChartType.bar, tornadoSeries, {
    x: leftX,
    y: pptTheme.layout.slidePaddingY,
    w: halfWidth,
    h: pptTheme.layout.chartHeight,
    // Without a title + legend the side-by-side pair is unreadable — nothing
    // says which chart is which or which colour maps to which entity.
    showTitle: true,
    title: `${baselineEntity.dim} vs ${focusEntity.dim}`,
    titleColor: toPptxHex(pptTheme.colors.text),
    titleFontFace: pptTheme.fonts.face,
    titleFontSize: 11,
    barDir: 'bar',
    // Clustered with full overlap looks identical to stacked here (the two
    // series extend opposite ways from zero) but, unlike stacked, allows
    // outEnd data labels — outEnd on a stacked bar corrupts the file.
    barGrouping: 'clustered',
    barOverlapPct: 100,
    showLegend: true,
    legendPos: 'b',
    legendColor: toPptxHex(pptTheme.colors.text),
    legendFontSize: 10,
    chartColors: [
      toPptxHex(pptTheme.colors.other),
      toPptxHex(pptTheme.colors.primary),
    ],
    showValue: true,
    dataLabelPosition: 'outEnd',
    dataLabelFormatCode: unsignedValueFormatCode,
    catAxisLabelFontSize: 10,
    catAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelFontSize: pptTheme.fonts.axisLabelSize,
    valGridLine: { color: toPptxHex(pptTheme.colors.grid), style: 'solid' },
    valAxisLabelFormatCode: valueFormatCode,
    catAxisLabelPos: 'low',
  });

  slide.addChart(PptxChartType.bar, differenceSeries, {
    x: rightX,
    y: pptTheme.layout.slidePaddingY,
    w: halfWidth,
    h: pptTheme.layout.chartHeight,
    showTitle: true,
    title: `Net change (${focusEntity.dim} - ${baselineEntity.dim})`,
    titleColor: toPptxHex(pptTheme.colors.text),
    titleFontFace: pptTheme.fonts.face,
    titleFontSize: 11,
    barDir: 'bar',
    barGrouping: 'clustered',
    barOverlapPct: 100,
    showLegend: false,
    chartColors: [
      toPptxHex(pptTheme.colors.divergingNegative),
      toPptxHex(pptTheme.colors.divergingPositive),
    ],
    showValue: true,
    dataLabelPosition: 'outEnd',
    dataLabelFormatCode: signedValueFormatCode,
    catAxisLabelFontSize: 10,
    catAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelFontSize: pptTheme.fonts.axisLabelSize,
    valGridLine: { color: toPptxHex(pptTheme.colors.grid), style: 'solid' },
    catAxisHidden: true,
    valAxisLabelFormatCode: valueFormatCode,
  });
};

export const addTextSlide = async (
  pptx: PptxGenJS,
  title: string,
  markdownContent: string,
  exportTitle?: string
) => {
  const { markdownToPptxSlideSegments } = await import(
    './ppt-markdown-converter'
  );
  const slide = pptx.addSlide();
  const slideHeader = exportTitle || title;

  slide.addText(slideHeader, {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: pptTheme.layout.header.w,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const segments = markdownToPptxSlideSegments(markdownContent);
  let y = 1.0;
  const bodyW = 9;
  const x = 0.5;
  const maxY = 5.2;

  for (const seg of segments) {
    if (seg.kind === 'text' && seg.runs.length > 0) {
      const h = Math.min(4.0, maxY - y);
      if (h < 0.2) {
        break;
      }
      slide.addText(seg.runs, {
        x,
        y,
        w: bodyW,
        h,
        color: pptTheme.colors.text,
        fontFace: pptTheme.fonts.face,
        fontSize: 11,
        valign: 'top',
        paraSpaceAfter: 6,
      });
      y += h + 0.05;
    } else if (seg.kind === 'table' && seg.rows.length > 0) {
      const tableRows: PptxGenJS.TableRow[] = seg.rows.map((row) =>
        row.map((cell) => ({ text: cell }))
      );
      const rowCount = tableRows.length;
      const tableH = Math.min(0.35 * rowCount + 0.1, maxY - y);
      if (tableH < 0.15) {
        break;
      }
      slide.addTable(tableRows, {
        x,
        y,
        w: bodyW,
        h: tableH,
        fontSize: 10,
        border: {
          type: 'solid',
          pt: 0.5,
          color: toPptxHex(pptTheme.colors.grid),
        },
        color: pptTheme.colors.text,
        fontFace: pptTheme.fonts.face,
      });
      y += tableH + 0.15;
    }
  }
};

export const addTableSlide = (
  pptx: PptxGenJS,
  sheetData: Record<string, string | number>[],
  headerTitle: string,
  exportTitle?: string
) => {
  const slide = pptx.addSlide();
  const slideHeader = exportTitle || headerTitle;

  slide.addText(slideHeader, {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: pptTheme.layout.header.w,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const firstRow = sheetData?.[0];
  if (!firstRow) {
    return;
  }

  const columns = Object.keys(firstRow);
  if (columns.length === 0) {
    return;
  }

  const tableWidth = 9;
  const columnWidth = tableWidth / columns.length;
  const colW = columns.map(() => columnWidth);

  const brandFill = toPptxHex(pptTheme.colors.brandPrimary);
  const whiteFill = toPptxHex(pptTheme.colors.white);
  const zebraFill = toPptxHex(pptTheme.colors.grid);
  const textColor = toPptxHex(pptTheme.colors.text);
  const whiteText = toPptxHex(pptTheme.colors.white);

  const headerRow: PptxGenJS.TableRow = columns.map((col) => ({
    text: col,
    options: {
      bold: true,
      fill: { color: brandFill },
      color: whiteText,
      fontSize: 9,
    },
  }));

  const dataRows: PptxGenJS.TableRow[] = sheetData.map((row, rowIndex) => {
    const fillColor = rowIndex % 2 === 0 ? whiteFill : zebraFill;
    return columns.map((col) => ({
      text: String(row[col] ?? ''),
      options: {
        fill: { color: fillColor },
      },
    }));
  });

  const allRows: PptxGenJS.TableRow[] = [headerRow, ...dataRows];

  slide.addTable(allRows, {
    x: 0.5,
    y: 1.0,
    w: tableWidth,
    colW,
    fontSize: 8,
    border: {
      type: 'solid',
      pt: 0.5,
      color: toPptxHex(pptTheme.colors.grid),
    },
    autoPage: true,
    autoPageRepeatHeader: true,
    autoPageHeaderRows: 1,
    newSlideStartY: 0.5,
    fontFace: pptTheme.fonts.face,
    color: textColor,
  });
};

export const addMixedStatsSlide = (
  pptx: PptxGenJS,
  charts: MixedStatsChart[],
  headerTitle: string,
  exportTitle?: string
) => {
  const slide = pptx.addSlide();
  const slideHeader = exportTitle || headerTitle;
  const headerWidth = pptTheme.layout.header.w;
  slide.addText(slideHeader, {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: headerWidth,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const chartWidth = 4.2;
  const chartHeight = 1.4;
  const colGap = 0.3;
  const rowGap = 0.2;
  const startX = 0.5;
  const startY = 0.8;

  const formatLatest = (value: number) => {
    const abs = Math.abs(value ?? 0);
    if (abs <= 2) return `${(value * 100).toFixed(1)}%`;
    return (value ?? 0).toLocaleString();
  };
  const formatDeltaPct = (delta: number) => {
    const sign = delta >= 0 ? '+' : '';
    return `${sign}${delta.toFixed(1)}%`;
  };

  charts.slice(0, 6).forEach((chart, index) => {
    const row = Math.floor(index / 2);
    const col = index % 2;
    const x = startX + col * (chartWidth + colGap);
    const y = startY + row * (chartHeight + rowGap);

    slide.addChart(
      PptxChartType.area,
      [
        {
          name: chart.name,
          labels: normalizeLabels(chart.labels),
          values: chart.values,
        },
      ],
      {
        x,
        y,
        w: chartWidth,
        h: chartHeight,
        showLegend: false,
        catAxisHidden: true,
        catGridLine: { style: 'none' },
        valAxisHidden: true,
        valAxisDisplayUnitLabel: true,
        valGridLine: { style: 'none' },
        showTitle: true,
        title: chart.name,
        chartColors: [pptTheme.colors.areaPrimary.replace('#', '')],
        chartColorsOpacity: 60,
      }
    );

    const lastValue = chart.lastValue ?? 0;
    slide.addText(formatLatest(lastValue), {
      x: x + chartWidth - 1.6,
      y: y + 0.1,
      w: 1.5,
      h: 0.2,
      fontSize: 10,
      bold: true,
      color: pptTheme.colors.text,
      align: 'right',
    });

    slide.addText(formatDeltaPct(chart.deltaPercentage ?? 0), {
      x: x + chartWidth - 1.6,
      y: y + 0.32,
      w: 1.5,
      h: 0.2,
      fontSize: 8,
      color:
        (chart.deltaPercentage ?? 0) >= 0
          ? pptTheme.colors.positive
          : pptTheme.colors.negative,
      align: 'right',
    });
  });
};

export type FlowFigures = {
  rows: { from: string; to: string; value: number }[];
  /** Sum over every flow in the dataset, not just the listed top rows. */
  total: number;
};

const FIGURES_PANEL_W = 2.6;
const FIGURES_PANEL_GAP = 0.2;

const compactCount = (value: number): string =>
  new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  })
    .format(value)
    .toLowerCase();

const truncateLabel = (label: string, max: number): string =>
  label.length > max ? `${label.slice(0, max - 1)}…` : label;

/**
 * Slide for a chart PowerPoint can't draw natively (Sankey / chord / treemap).
 * Same header as a native chart slide, with the captured chart placed as a
 * picture scaled to fit the chart box without distortion. A picture carries no
 * readable scale, so flow charts also get a "Largest transitions" side panel
 * with the top flows and the dataset total.
 */
export const addChartImageSlide = (
  slide: PptxGenJS.Slide,
  title: string,
  exportTitle: string | undefined,
  image: CapturedChartImage,
  figures?: FlowFigures
) => {
  const slideTitle = exportTitle;

  slide.addText(slideTitle || title, {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: pptTheme.layout.header.w,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const hasFigures = !!figures && figures.rows.length > 0;
  const imageW = hasFigures
    ? pptTheme.layout.chartWidth - FIGURES_PANEL_W - FIGURES_PANEL_GAP
    : pptTheme.layout.chartWidth;

  slide.addImage({
    data: image.dataUrl,
    ...fitImageToBox(image, {
      x: pptTheme.layout.slidePaddingX,
      y: pptTheme.layout.slidePaddingY,
      w: imageW,
      h: pptTheme.layout.chartHeight,
    }),
  });

  if (!hasFigures) return;

  const panelX = pptTheme.layout.slidePaddingX + imageW + FIGURES_PANEL_GAP;
  const textColor = toPptxHex(pptTheme.colors.text);

  slide.addText('Largest transitions', {
    x: panelX,
    y: pptTheme.layout.slidePaddingY,
    w: FIGURES_PANEL_W,
    h: 0.3,
    fontSize: 11,
    bold: true,
    color: textColor,
    fontFace: pptTheme.fonts.face,
    align: 'left',
  });

  const figureRows: PptxGenJS.TableRow[] = figures.rows.map((row) => [
    {
      text: `${truncateLabel(row.from, 16)} → ${truncateLabel(row.to, 16)}`,
      options: { align: 'left' as const },
    },
    {
      text: compactCount(row.value),
      options: { align: 'right' as const },
    },
  ]);
  figureRows.push([
    { text: 'Total', options: { align: 'left' as const, bold: true } },
    {
      text: compactCount(figures.total),
      options: { align: 'right' as const, bold: true },
    },
  ]);

  slide.addTable(figureRows, {
    x: panelX,
    y: pptTheme.layout.slidePaddingY + 0.35,
    w: FIGURES_PANEL_W,
    colW: [FIGURES_PANEL_W - 0.65, 0.65],
    fontSize: 8,
    fontFace: pptTheme.fonts.face,
    color: textColor,
    border: {
      type: 'solid',
      pt: 0.5,
      color: toPptxHex(pptTheme.colors.grid),
    },
    valign: 'middle',
  });
};

type BoxPlotCategory = { group: string; entity: string; box: BoxDataPoint };

export type BoxPlotEntityColor = { entity: string; color: string };

export type BoxPlotChartSpec = {
  series: PptChartSeries[];
  colors: string[];
  valAxisMinVal: number;
  valAxisMaxVal: number;
  caption: string;
};

const BOX_PLOT_OFFSET_SERIES_NAME = ' ';
const BOX_PLOT_OFFSET_COLOR = 'transparent';
const BOX_PLOT_QUARTILE_BANDS = [
  'P10-P25',
  'P25-Median',
  'Median-P75',
  'P75-P90',
];
const BOX_PLOT_BANDS = ['P10-Median', 'Median-P90'];
const BOX_PLOT_QUARTILE_CAPTION =
  'Bars span P10-P90 · pale ends are the P10-P25 and P75-P90 whiskers · the shade changes at the median';
const BOX_PLOT_CAPTION = 'Bars span P10-P90 · the shade changes at the median';
const BOX_PLOT_AXIS_PADDING_RATIO = 0.08;

export const isBoxPlotDataSet = (
  value: unknown
): value is BreakdownBoxDataSet => {
  if (typeof value !== 'object' || value === null) return false;
  const dataSet = value as BreakdownBoxDataSet;
  if (dataSet.type !== DataType.BOX || !Array.isArray(dataSet.data)) {
    return false;
  }
  return dataSet.data.every(
    (group) => group !== null && Array.isArray(group?.breakdown)
  );
};

const flattenBoxPlotCategories = (
  dataSet: BreakdownBoxDataSet
): BoxPlotCategory[] =>
  dataSet.data.flatMap((group) =>
    group.breakdown
      .filter((entry) =>
        [entry.box?.q10, entry.box?.q50, entry.box?.q90].every((v) =>
          Number.isFinite(v)
        )
      )
      .map((entry) => ({
        group: group.dim,
        entity: entry.dim,
        box: entry.box,
      }))
  );

const buildBoxPlotLabels = (categories: BoxPlotCategory[]): string[] => {
  const isNested = categories.some((c) => c.entity !== c.group);
  return categories.map((c) =>
    isNested ? `${c.group} - ${c.entity}` : c.group
  );
};

const boxPlotStops = (box: BoxDataPoint, withQuartiles: boolean): number[] =>
  withQuartiles
    ? [box.q10, box.q25 ?? box.q10, box.q50, box.q75 ?? box.q90, box.q90]
    : [box.q10, box.q50, box.q90];

const boxPlotBandShades = (
  entityColor: string,
  bandCount: number
): string[] => {
  const strong = toPptxHex(entityColor);
  const median = lightenHexColor(strong, 0.4);
  const whisker = lightenHexColor(strong, 0.68);
  return bandCount === 4
    ? [whisker, strong, median, whisker]
    : [strong, median];
};

const boxPlotAxisBounds = (
  low: number,
  high: number
): { valAxisMinVal: number; valAxisMaxVal: number } => {
  const range = high - low;
  const padding = range > 0 ? range * BOX_PLOT_AXIS_PADDING_RATIO : 1;
  const paddedLow = low - padding;
  const paddedHigh = high + padding;
  const step =
    10 ** Math.floor(Math.log10(Math.max(1, paddedHigh - paddedLow)));
  const flooredLow = Math.floor(paddedLow / step) * step;
  return {
    valAxisMinVal: low >= 0 ? Math.max(0, flooredLow) : flooredLow,
    valAxisMaxVal: Math.ceil(paddedHigh / step) * step,
  };
};

const resolveBoxPlotEntityColors = (
  categories: BoxPlotCategory[],
  entityColors: BoxPlotEntityColor[] | undefined
): Map<string, string> => {
  const resolved = new Map<string, string>();
  const palette = pptTheme.colors.chartPalette;
  for (const category of categories) {
    if (resolved.has(category.entity)) continue;
    const onScreen = entityColors?.find((e) => e.entity === category.entity);
    resolved.set(
      category.entity,
      onScreen?.color ??
        palette[resolved.size % palette.length] ??
        palette[0] ??
        ''
    );
  }
  return resolved;
};

export const buildBoxPlotChartSpec = (
  dataSet: BreakdownBoxDataSet,
  entityColors?: BoxPlotEntityColor[]
): BoxPlotChartSpec | null => {
  const categories = flattenBoxPlotCategories(dataSet);
  if (categories.length === 0) return null;

  const colorByEntity = resolveBoxPlotEntityColors(categories, entityColors);

  const plotted = [...categories].reverse();

  const labels = buildBoxPlotLabels(plotted);
  const withQuartiles = plotted.every(
    (c) => typeof c.box.q25 === 'number' && typeof c.box.q75 === 'number'
  );
  const bandNames = withQuartiles ? BOX_PLOT_QUARTILE_BANDS : BOX_PLOT_BANDS;
  const stops = plotted.map((c) => boxPlotStops(c.box, withQuartiles));

  const series: PptChartSeries[] = [
    {
      name: BOX_PLOT_OFFSET_SERIES_NAME,
      labels,
      values: stops.map((s) => s[0] ?? 0),
    },
  ];
  const colors: string[] = [BOX_PLOT_OFFSET_COLOR];

  const namePerEntity = colorByEntity.size > 1;
  for (const [entity, entityColor] of colorByEntity) {
    const shades = boxPlotBandShades(entityColor, bandNames.length);
    bandNames.forEach((bandName, bandIndex) => {
      series.push({
        name: namePerEntity ? `${entity} - ${bandName}` : bandName,
        labels,
        values: plotted.map((category, categoryIndex) => {
          if (category.entity !== entity) return 0;
          const categoryStops = stops[categoryIndex] ?? [];
          return Math.max(
            0,
            (categoryStops[bandIndex + 1] ?? 0) -
              (categoryStops[bandIndex] ?? 0)
          );
        }),
      });
      colors.push(shades[bandIndex] ?? shades[0] ?? '');
    });
  }

  return {
    series,
    colors,
    caption: withQuartiles ? BOX_PLOT_QUARTILE_CAPTION : BOX_PLOT_CAPTION,
    ...boxPlotAxisBounds(
      Math.min(...categories.map((c) => c.box.q10)),
      Math.max(...categories.map((c) => c.box.q90))
    ),
  };
};

const boxPlotChartOptions = (
  spec: BoxPlotChartSpec,
  fonts: { titleSize: number; axisLabelSize: number }
): PptxGenJS.IChartOpts => ({
  barDir: 'bar',
  barGrouping: 'stacked',
  chartColors: spec.colors,
  valAxisMinVal: spec.valAxisMinVal,
  valAxisMaxVal: spec.valAxisMaxVal,
  valAxisLabelFormatCode: '#,##0',
  showLegend: false,
  catAxisLabelColor: toPptxHex(pptTheme.colors.text),
  catAxisLabelFontSize: fonts.axisLabelSize,
  valAxisLabelColor: toPptxHex(pptTheme.colors.text),
  valAxisLabelFontSize: fonts.axisLabelSize,
  valGridLine: { color: toPptxHex(pptTheme.colors.grid), style: 'solid' },
  titleColor: toPptxHex(pptTheme.colors.text),
  titleAlign: 'left',
  titleFontFace: pptTheme.fonts.face,
  titleFontSize: fonts.titleSize,
});

const addBoxPlotCaption = (
  slide: PptxGenJS.Slide,
  caption: string,
  layout: ReportSlotLayout & { fontSize: number }
) => {
  slide.addText(caption, {
    x: layout.x,
    y: layout.y,
    w: layout.w,
    h: layout.h,
    fontSize: layout.fontSize,
    fontFace: pptTheme.fonts.face,
    color: pptTheme.colors.text,
    align: 'left',
    valign: 'top',
  });
};

const BOX_PLOT_CAPTION_HEIGHT = 0.25;

export const addBoxPlotSlide = (
  slide: PptxGenJS.Slide,
  title: string,
  exportTitle: string | undefined,
  spec: BoxPlotChartSpec
) => {
  const slideHeader = exportTitle || title;

  slide.addText(slideHeader, {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: pptTheme.layout.header.w,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const hasTitle = title.trim().length > 0;
  slide.addChart(PptxChartType.bar, spec.series, {
    ...boxPlotChartOptions(spec, {
      titleSize: pptTheme.fonts.titleSize,
      axisLabelSize: pptTheme.fonts.axisLabelSize,
    }),
    x: pptTheme.layout.slidePaddingX,
    y: pptTheme.layout.slidePaddingY,
    w: pptTheme.layout.chartWidth,
    h: pptTheme.layout.chartHeight,
    showTitle: hasTitle,
    title: hasTitle ? title : '',
  });

  addBoxPlotCaption(slide, spec.caption, {
    x: pptTheme.layout.slidePaddingX,
    y: pptTheme.layout.slidePaddingY + pptTheme.layout.chartHeight,
    w: pptTheme.layout.chartWidth,
    h: BOX_PLOT_CAPTION_HEIGHT,
    fontSize: 9,
  });
};

const BOX_PLOT_MIN_HEIGHT_FOR_CAPTION = 1.4;

export const addReportBoxPlotToSlide = (
  slide: PptxGenJS.Slide,
  spec: BoxPlotChartSpec,
  opts: ReportSlotLayout & { title?: string }
) => {
  const { x, y, w, h, title } = opts;
  const titleTrim = title?.trim() ?? '';
  const showCaption = h >= BOX_PLOT_MIN_HEIGHT_FOR_CAPTION;
  const captionH = showCaption ? BOX_PLOT_CAPTION_HEIGHT : 0;

  slide.addChart(PptxChartType.bar, spec.series, {
    ...boxPlotChartOptions(spec, {
      titleSize: reportPptTheme.fonts.titleSize,
      axisLabelSize: reportPptTheme.fonts.axisLabelSize,
    }),
    x,
    y,
    w,
    h: h - captionH,
    showTitle: titleTrim.length > 0,
    title: titleTrim,
  });

  if (showCaption) {
    addBoxPlotCaption(slide, spec.caption, {
      x,
      y: y + h - captionH,
      w,
      h: captionH,
      fontSize: reportPptTheme.fonts.dataLabelSize,
    });
  }
};

export const addSlideContent = (
  slide: PptxGenJS.Slide,
  title: string,
  exportTitle: string | undefined,
  chartData: unknown,
  pptChart: { type: PptxGenJS.CHART_NAME; opts?: Record<string, unknown> },
  chartType?: ReplotChartNames | string,
  selectedSegments?: { name: string }[],
  yAxis?: YAxisBounds
) => {
  const slideTitle = exportTitle;

  const headerWidth = pptTheme.layout.header.w;
  slide.addText(slideTitle || '', {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: headerWidth,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  if (!chartData) return;

  let chartTitle = title;

  if (Array.isArray(selectedSegments) && selectedSegments.length > 0) {
    const segmentNames = selectedSegments.map((s) => s.name).join(', ');
    chartTitle = `${title} - ${segmentNames}`;
  }

  const chartWidth = pptTheme.layout.chartWidth;
  const chartHeight = pptTheme.layout.chartHeight;

  if (
    chartType === ReplotChartNames.VolumeChart &&
    isVolumeChartData(chartData)
  ) {
    if (!chartData.barData[0] || !chartData.barData[1]) {
      console.warn('Invalid volume chart data: missing bar data');
      return;
    }

    const comboTypes: PptxGenJS.IChartMulti[] = [
      {
        type: PptxChartType.bar,
        data: [
          {
            name: chartData.barData[0].name,
            labels: chartData.barData[0].labels,
            values: chartData.barData[0].values,
          },
        ],
        options: {
          chartColors: [toPptxHex(pptTheme.colors.positive)],
          x: pptTheme.layout.slidePaddingX,
          y: pptTheme.layout.slidePaddingY,
          w: chartWidth,
          h: chartHeight,
          title: chartTitle,
          showTitle: true,
          titleColor: toPptxHex(pptTheme.colors.text),
          titleAlign: 'left',
          titleFontFace: pptTheme.fonts.face,
          titleFontSize: pptTheme.fonts.titleSize,
          legendColor: toPptxHex(pptTheme.colors.text),
          legendFontSize: pptTheme.fonts.legendSize,
          catAxisLabelColor: toPptxHex(pptTheme.colors.text),
          catAxisLabelFontSize: pptTheme.fonts.axisLabelSize,
          valAxisLabelColor: toPptxHex(pptTheme.colors.text),
          valAxisLabelFontSize: pptTheme.fonts.axisLabelSize,
          dataLabelColor: toPptxHex(pptTheme.colors.text),
          dataLabelFontSize: pptTheme.fonts.dataLabelSize,
          valGridLine: {
            color: toPptxHex(pptTheme.colors.grid),
            style: 'solid',
          },
          showLegend: true,
          legendPos: 'b',
          barDir: 'col',
          barGrouping: 'stacked',
        },
      },
      {
        type: PptxChartType.bar,
        data: [
          {
            name: chartData.barData[1].name,
            labels: chartData.barData[1].labels,
            values: chartData.barData[1].values.map((v) => -v),
          },
        ],
        options: {
          chartColors: [toPptxHex(pptTheme.colors.negative)],
        },
      },
      {
        type: PptxChartType.area,
        data: chartData.lineData,
        options: {
          chartColors: [toPptxHex(pptTheme.colors.areaSecondary)],
          lineDataSymbol: 'none',
          chartColorsOpacity: 60,
          lineSize: 2,
        },
      },
    ];

    // @ts-expect-error Typings are incorrect. PPTXGenJS errors out when passing a data array in addition to a MultiChart array.
    slide.addChart(comboTypes);
    return;
  }

  if (!isChartSeriesArray(chartData)) {
    console.warn('Invalid chart data format, expected array of chart series');
    return;
  }

  const chartOptions = getChartOptions(chartTitle, pptChart);

  const mutableChartOptions: PptxGenJS.IChartOpts = {
    ...chartOptions,
    ...lineAxisOverrides(chartType, yAxis, chartData),
    chartColors: chartOptions.chartColors
      ? Array.from(chartOptions.chartColors)
      : undefined,
  };
  applyDataLabelFormat(mutableChartOptions, chartData);

  slide.addChart(pptChart.type, chartData, mutableChartOptions);
};

export type ReportSlotLayout = {
  x: number;
  y: number;
  w: number;
  h: number;
};

const reportChartFontOverrides = {
  titleFontSize: reportPptTheme.fonts.titleSize,
  legendFontSize: reportPptTheme.fonts.legendSize,
  catAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
  valAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
  dataLabelFontSize: reportPptTheme.fonts.dataLabelSize,
} as const;

const lineChartReportOverrides = {
  catAxisLabelFrequency: '12',
  catLabelFormatCode: 'yyyy',
} as const;

export type YAxisBounds = { min: number; max: number };

const LINE_AXIS_TICK_COUNT = 5;

const stepDecimals = (step: number) => {
  let decimals = 0;
  while (
    decimals < 4 &&
    Math.abs(step * 10 ** decimals - Math.round(step * 10 ** decimals)) > 1e-9
  ) {
    decimals += 1;
  }
  return decimals;
};

const lineAxisFormatCode = (step: number, asPercent: boolean) => {
  const decimals =
    Number.isFinite(step) && step > 0
      ? stepDecimals(asPercent ? step * 100 : step)
      : 0;
  const fraction = decimals > 0 ? `.${'0'.repeat(decimals)}` : '';
  return asPercent ? `0${fraction}%` : `#,##0${fraction}`;
};

/** A native line chart's value axis as the card draws it: shared bounds niced to round ticks, fractions as percents. */
const lineAxisOverrides = (
  chartType: ReplotChartNames | string | undefined,
  yAxis: YAxisBounds | undefined,
  chartData: { values?: (number | null)[] }[]
): Pick<
  PptxGenJS.IChartOpts,
  | 'valAxisMinVal'
  | 'valAxisMaxVal'
  | 'valAxisMajorUnit'
  | 'valAxisLabelFormatCode'
> => {
  if (chartType !== ReplotChartNames.LineChart) return {};
  const asPercent = isFractionScale(chartData);
  if (!yAxis || !(yAxis.max > yAxis.min)) {
    const [low, high] = extent(
      chartData.flatMap((series) => series.values ?? []),
      (value) => value ?? undefined
    );
    if (low === undefined || high === undefined) return {};
    return {
      valAxisLabelFormatCode: lineAxisFormatCode(
        tickStep(low, high, LINE_AXIS_TICK_COUNT),
        asPercent
      ),
    };
  }
  const [min = yAxis.min, max = yAxis.max] = scaleLinear()
    .domain([yAxis.min, yAxis.max])
    .nice(LINE_AXIS_TICK_COUNT)
    .domain();
  const step = tickStep(min, max, LINE_AXIS_TICK_COUNT);
  return {
    valAxisMinVal: min,
    valAxisMaxVal: max,
    ...(Number.isFinite(step) && step > 0 && { valAxisMajorUnit: step }),
    valAxisLabelFormatCode: lineAxisFormatCode(step, asPercent),
  };
};

const reportChartTitleWithSegments = (
  title: string,
  selectedSegments?: { name: string }[]
): string => {
  const base = title.trim();
  if (Array.isArray(selectedSegments) && selectedSegments.length > 0) {
    const segmentNames = selectedSegments.map((s) => s.name).join(', ');
    return base ? `${base} - ${segmentNames}` : segmentNames;
  }
  return base;
};

export const addReportChartToSlide = (
  slide: PptxGenJS.Slide,
  chartData: unknown,
  pptChart: { type: PptxGenJS.CHART_NAME; opts?: Record<string, unknown> },
  opts: ReportSlotLayout & {
    title: string;
    chartType?: ReplotChartNames | string;
    selectedSegments?: { name: string }[];
    yAxis?: YAxisBounds;
  }
) => {
  const { x, y, w, h, title, chartType, selectedSegments, yAxis } = opts;

  if (!chartData) return;

  const titleWithSegments = reportChartTitleWithSegments(
    title,
    selectedSegments
  );
  const hasTitle = titleWithSegments.trim().length > 0;

  if (
    chartType === ReplotChartNames.VolumeChart &&
    isVolumeChartData(chartData)
  ) {
    if (!chartData.barData[0] || !chartData.barData[1]) {
      console.warn('Invalid volume chart data: missing bar data');
      return;
    }

    const comboTypes: PptxGenJS.IChartMulti[] = [
      {
        type: PptxChartType.bar,
        data: [
          {
            name: chartData.barData[0].name,
            labels: chartData.barData[0].labels,
            values: chartData.barData[0].values,
          },
        ],
        options: {
          chartColors: [toPptxHex(pptTheme.colors.positive)],
          x,
          y,
          w,
          h,
          title: hasTitle ? titleWithSegments : '',
          showTitle: hasTitle,
          titleColor: toPptxHex(pptTheme.colors.text),
          titleAlign: 'left',
          titleFontFace: pptTheme.fonts.face,
          ...reportChartFontOverrides,
          legendColor: toPptxHex(pptTheme.colors.text),
          catAxisLabelColor: toPptxHex(pptTheme.colors.text),
          valAxisLabelColor: toPptxHex(pptTheme.colors.text),
          dataLabelColor: toPptxHex(pptTheme.colors.text),
          valGridLine: {
            color: toPptxHex(pptTheme.colors.grid),
            style: 'solid',
          },
          showLegend: true,
          legendPos: 'b',
          barDir: 'col',
          barGrouping: 'stacked',
        },
      },
      {
        type: PptxChartType.bar,
        data: [
          {
            name: chartData.barData[1].name,
            labels: chartData.barData[1].labels,
            values: chartData.barData[1].values.map((v) => -v),
          },
        ],
        options: {
          chartColors: [toPptxHex(pptTheme.colors.negative)],
        },
      },
      {
        type: PptxChartType.area,
        data: chartData.lineData,
        options: {
          chartColors: [toPptxHex(pptTheme.colors.areaSecondary)],
          lineDataSymbol: 'none',
          chartColorsOpacity: 60,
          lineSize: 2,
        },
      },
    ];
    // @ts-expect-error Typings are incorrect. PPTXGenJS errors out when passing a data array in addition to a MultiChart array.
    slide.addChart(comboTypes);
    return;
  }

  if (!isChartSeriesArray(chartData)) {
    console.warn('Invalid chart data format, expected array of chart series');
    return;
  }

  const baseChartOptions = getChartOptions(titleWithSegments || ' ', pptChart);
  const lineOverrides =
    chartType === ReplotChartNames.LineChart ? lineChartReportOverrides : {};
  const mutableChartOptions: PptxGenJS.IChartOpts = {
    ...baseChartOptions,
    ...reportChartFontOverrides,
    ...lineOverrides,
    ...lineAxisOverrides(chartType, yAxis, chartData),
    x,
    y,
    w,
    h,
    showTitle: hasTitle,
    title: hasTitle ? titleWithSegments : '',
    chartColors: baseChartOptions.chartColors
      ? Array.from(baseChartOptions.chartColors)
      : undefined,
  };
  applyDataLabelFormat(mutableChartOptions, chartData);

  slide.addChart(pptChart.type, chartData, mutableChartOptions);
};

export const addReportDivergingBarComboToSlide = (
  slide: PptxGenJS.Slide,
  rawData: unknown,
  opts: ReportSlotLayout & {
    title: string;
    dataType?: string;
    selectedSegments?: { name: string }[];
  }
) => {
  const { x, y, w, h, title, dataType, selectedSegments } = opts;

  if (!isBreakdownSingleTwoEntity(rawData)) {
    const { sheetData } = buildSheetData(
      rawData,
      dataType,
      ReplotChartNames.DivergingBarChart
    );
    if (!sheetData || sheetData.length === 0) {
      console.warn('No sheet data for diverging bar combo fallback');
      return;
    }
    const chartData = generateChartData(
      sheetData,
      ReplotChartNames.DivergingBarChart
    );
    const pptChart = {
      type: PptxChartType.bar as PptxGenJS.CHART_NAME,
      opts: {
        barDir: 'bar',
        barGrouping: 'stacked',
        showLegend: false,
        axisLabelFormatCode: '#%',
        catAxisLabelPos: 'low',
        valueBarColors: true,
        chartColors: [
          pptTheme.colors.divergingPositive.replace('#', ''),
          pptTheme.colors.divergingNegative.replace('#', ''),
        ],
        catAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
      },
    };
    addReportChartToSlide(slide, chartData, pptChart, {
      x,
      y,
      w,
      h,
      title,
      chartType: ReplotChartNames.DivergingBarChart,
      selectedSegments,
    });
    return;
  }

  const titleTrim = title.trim();
  const headerH = titleTrim ? 0.3 : 0;
  if (titleTrim) {
    slide.addText(titleTrim, {
      x,
      y,
      w,
      h: headerH,
      fontSize: reportPptTheme.fonts.titleSize,
      bold: true,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'left',
    });
  }

  const focusEntity = rawData.data[0];
  const baselineEntity = rawData.data[1];
  const useShare = breakdownHasShares(rawData);

  const dimensionSet = new Set<string>();
  for (const entity of rawData.data) {
    for (const point of entity.breakdown) {
      dimensionSet.add(point.dim);
    }
  }

  const dimensions = Array.from(dimensionSet);
  const rows = dimensions.map((dim) => {
    const baselineValue = getBreakdownMetric(baselineEntity, dim, useShare);
    const focusValue = getBreakdownMetric(focusEntity, dim, useShare);
    return {
      dim,
      leftValue: -baselineValue,
      rightValue: focusValue,
    };
  });
  rows.sort((a, b) => {
    const absA = Math.abs(getBreakdownMetric(baselineEntity, a.dim, useShare));
    const absB = Math.abs(getBreakdownMetric(baselineEntity, b.dim, useShare));
    if (absA !== absB) {
      return absA - absB;
    }
    return a.dim.localeCompare(b.dim);
  });

  const sortedLabels = rows.map((r) => r.dim);
  const leftValues = rows.map((r) => r.leftValue);
  const rightValues = rows.map((r) => r.rightValue);

  const tornadoSeries = [
    {
      name: baselineEntity.dim,
      labels: sortedLabels,
      values: leftValues,
    },
    {
      name: focusEntity.dim,
      labels: sortedLabels,
      values: rightValues,
    },
  ];

  const differenceValues = sortedLabels.map((dim) => {
    const leftValue = getBreakdownMetric(baselineEntity, dim, useShare);
    const rightValue = getBreakdownMetric(focusEntity, dim, useShare);
    return rightValue - leftValue;
  });

  const differenceSeries = [
    {
      name: 'Negative',
      labels: sortedLabels,
      values: differenceValues.map((d) => (d < 0 ? d : 0)),
    },
    {
      name: 'Positive',
      labels: sortedLabels,
      values: differenceValues.map((d) => (d > 0 ? d : 0)),
    },
  ];

  const valueFormatCode = getDivergingAxisFormat([
    ...leftValues,
    ...rightValues,
    ...differenceValues,
  ]);
  // pos;neg;zero format sections. The tornado's left side is negated only to
  // mirror the bars, so its labels drop the sign; the empty zero section hides
  // labels on zero points — the unused half of a mutually-exclusive pair.
  const unsignedValueFormatCode = `${valueFormatCode};${valueFormatCode};`;
  const signedValueFormatCode = `${valueFormatCode};-${valueFormatCode};`;

  const gap = 0.15;
  const halfW = (w - gap) / 2;
  const chartY = y + headerH + 0.05;
  const chartH = Math.max(0.5, h - headerH - 0.05);
  const leftX = x;
  const rightX = x + halfW + gap;

  slide.addChart(PptxChartType.bar, tornadoSeries, {
    x: leftX,
    y: chartY,
    w: halfW,
    h: chartH,
    showTitle: true,
    title: `${baselineEntity.dim} vs ${focusEntity.dim}`,
    titleColor: toPptxHex(pptTheme.colors.text),
    titleFontFace: pptTheme.fonts.face,
    titleFontSize: reportPptTheme.fonts.titleSize,
    barDir: 'bar',
    // Clustered with full overlap looks identical to stacked here (the two
    // series extend opposite ways from zero) but, unlike stacked, allows
    // outEnd data labels — outEnd on a stacked bar corrupts the file.
    barGrouping: 'clustered',
    barOverlapPct: 100,
    showLegend: true,
    legendPos: 'b',
    legendColor: toPptxHex(pptTheme.colors.text),
    legendFontSize: reportPptTheme.fonts.legendSize,
    chartColors: [
      toPptxHex(pptTheme.colors.other),
      toPptxHex(pptTheme.colors.primary),
    ],
    showValue: true,
    dataLabelPosition: 'outEnd',
    dataLabelFormatCode: unsignedValueFormatCode,
    catAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
    catAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
    valGridLine: { color: toPptxHex(pptTheme.colors.grid), style: 'solid' },
    valAxisLabelFormatCode: valueFormatCode,
    catAxisLabelPos: 'low',
  });

  slide.addChart(PptxChartType.bar, differenceSeries, {
    x: rightX,
    y: chartY,
    w: halfW,
    h: chartH,
    showTitle: true,
    title: `Net change (${focusEntity.dim} - ${baselineEntity.dim})`,
    titleColor: toPptxHex(pptTheme.colors.text),
    titleFontFace: pptTheme.fonts.face,
    titleFontSize: reportPptTheme.fonts.titleSize,
    barDir: 'bar',
    barGrouping: 'clustered',
    barOverlapPct: 100,
    showLegend: false,
    chartColors: [
      toPptxHex(pptTheme.colors.divergingNegative),
      toPptxHex(pptTheme.colors.divergingPositive),
    ],
    showValue: true,
    dataLabelPosition: 'outEnd',
    dataLabelFormatCode: signedValueFormatCode,
    catAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
    catAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
    valGridLine: { color: toPptxHex(pptTheme.colors.grid), style: 'solid' },
    catAxisHidden: true,
    valAxisLabelFormatCode: valueFormatCode,
  });
};

export const addGaugeSlide = (
  slide: PptxGenJS.Slide,
  title: string,
  exportTitle: string | undefined,
  gauge: {
    mainText?: string;
    value?: number;
    entities?: { label?: string; value: number }[];
    colors?: string[];
    minLabel?: string;
    maxLabel?: string;
  }
) => {
  const slideHeader = exportTitle || title;

  slide.addText(slideHeader, {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: pptTheme.layout.header.w,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const contentX = pptTheme.layout.slidePaddingX;
  const contentY = pptTheme.layout.slidePaddingY;
  const contentW = pptTheme.layout.chartWidth;
  const contentH = pptTheme.layout.chartHeight;

  // The slide header is the report/page name (the chart title is sliced off the
  // file name), so render the gauge's own title above the value the way other
  // chart types surface it inside their plot.
  const chartTitleTrim = title?.trim() ?? '';
  const showChartTitle =
    chartTitleTrim.length > 0 && chartTitleTrim !== slideHeader.trim();
  const chartTitleH = showChartTitle ? 0.5 : 0;
  if (showChartTitle) {
    slide.addText(chartTitleTrim, {
      x: contentX,
      y: contentY,
      w: contentW,
      h: chartTitleH,
      fontSize: pptTheme.fonts.titleSize,
      bold: true,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'center',
      valign: 'middle',
    });
  }

  renderGaugeContent(slide, {
    x: contentX,
    y: contentY + chartTitleH,
    w: contentW,
    h: Math.max(0.4, contentH - chartTitleH),
    mainText: gauge.mainText,
    minLabel: gauge.minLabel,
    maxLabel: gauge.maxLabel,
    value: gauge.value,
    entities: gauge.entities,
    colors: gauge.colors,
    subFontSize: reportPptTheme.fonts.gaugeSubTextSize,
  });
};

export const addReportGaugeToSlide = (
  slide: PptxGenJS.Slide,
  opts: ReportSlotLayout & {
    title?: string;
    mainText?: string;
    value?: number;
    entities?: { label?: string; value: number }[];
    colors?: string[];
    minLabel?: string;
    maxLabel?: string;
  }
) => {
  const {
    x,
    y,
    w,
    h,
    title,
    mainText,
    value,
    entities,
    colors,
    minLabel,
    maxLabel,
  } = opts;

  const titleTrim = title?.trim() ?? '';
  const headerH = titleTrim ? 0.3 : 0;
  if (titleTrim) {
    slide.addText(titleTrim, {
      x,
      y,
      w,
      h: headerH,
      fontSize: reportPptTheme.fonts.titleSize,
      bold: true,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'left',
    });
  }

  renderGaugeContent(slide, {
    x,
    y: y + headerH,
    w,
    h: Math.max(0.4, h - headerH),
    mainText,
    minLabel,
    maxLabel,
    value,
    entities,
    colors,
    subFontSize: reportPptTheme.fonts.gaugeSubTextSize,
  });
};

export type MarkdownSlideTextOptions = {
  /** `inline` keeps segments in document order; `stacked` shrink-fits all text above the tables. */
  layout: 'inline' | 'stacked';
  title?: string;
  bodyPt: number;
  headingSizes?: readonly number[];
  paragraphGapPt: number;
  tablePt: number;
  tableRowHIn: number;
  tableGapIn: number;
  borderColor?: string;
};

export const addMarkdownSegmentsToSlide = async (
  slide: PptxGenJS.Slide,
  markdownContent: string,
  { x, y, w, h }: ReportSlotLayout,
  {
    layout,
    title,
    bodyPt,
    headingSizes,
    paragraphGapPt,
    tablePt,
    tableRowHIn,
    tableGapIn,
    borderColor = toPptxHex(pptTheme.colors.grid),
  }: MarkdownSlideTextOptions
) => {
  const { markdownToPptxSlideSegments } = await import(
    './ppt-markdown-converter'
  );
  const segments = markdownToPptxSlideSegments(markdownContent, {
    headingSizes,
  });
  const textOptions = {
    color: toPptxHex(pptTheme.colors.text),
    fontFace: pptTheme.fonts.face,
    fontSize: bodyPt,
    valign: 'top',
    paraSpaceAfter: paragraphGapPt,
  } satisfies PptxGenJS.TextPropsOptions;
  const tableOptions = {
    fontSize: tablePt,
    border: { type: 'solid', pt: 0.5, color: borderColor },
    color: toPptxHex(pptTheme.colors.text),
    fontFace: pptTheme.fonts.face,
  } satisfies PptxGenJS.TableProps;

  const titleTrim = title?.trim() ?? '';
  const headerH = titleTrim ? 0.3 : 0;
  if (titleTrim) {
    slide.addText(titleTrim, {
      x,
      y,
      w,
      h: headerH,
      fontSize: reportPptTheme.fonts.titleSize,
      bold: true,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'left',
    });
  }
  const top = y + headerH;
  const maxY = y + h;

  if (layout === 'stacked') {
    // Tables take their fixed height; text segments share the rest by length, all in source order.
    const entries = segments.filter((segment) =>
      segment.kind === 'text'
        ? segment.runs.length > 0
        : segment.rows.length > 0
    );
    const tablesH = entries.reduce(
      (sum, segment) =>
        segment.kind === 'table'
          ? sum + segment.rows.length * tableRowHIn + tableGapIn
          : sum,
      0
    );
    const textChars = entries.map((segment) =>
      segment.kind === 'text'
        ? Math.max(
            1,
            segment.runs.reduce((sum, run) => sum + (run.text?.length ?? 0), 0)
          )
        : 0
    );
    const totalChars = Math.max(
      1,
      textChars.reduce((sum, chars) => sum + chars, 0)
    );
    const textTotalH = Math.max(0.3, maxY - top - tablesH);
    let cursorY = top;
    entries.forEach((segment, index) => {
      if (segment.kind === 'text') {
        const segH = Math.max(
          0.3,
          (textTotalH * (textChars[index] ?? 0)) / totalChars
        );
        slide.addText(segment.runs, {
          ...textOptions,
          x,
          y: cursorY,
          w,
          h: segH,
          margin: 0,
          lineSpacingMultiple: 1.1,
          fit: 'shrink',
        });
        cursorY += segH;
        return;
      }
      slide.addTable(
        segment.rows.map((row, rowIndex) =>
          row.map((cell) => ({ text: cell, options: { bold: rowIndex === 0 } }))
        ),
        { ...tableOptions, x, y: cursorY, w }
      );
      cursorY += segment.rows.length * tableRowHIn + tableGapIn;
    });
    return;
  }

  let cursorY = top;
  for (const seg of segments) {
    if (seg.kind === 'text' && seg.runs.length > 0) {
      const available = maxY - cursorY;
      if (available < 0.2) break;
      const segH = Math.min(4.0, available);
      slide.addText(seg.runs, {
        ...textOptions,
        x,
        y: cursorY,
        w,
        h: segH,
      });
      cursorY += segH + 0.05;
    } else if (seg.kind === 'table' && seg.rows.length > 0) {
      const tableRows: PptxGenJS.TableRow[] = seg.rows.map((row) =>
        row.map((cell) => ({ text: cell }))
      );
      const tableH = Math.min(
        tableRowHIn * tableRows.length + tableGapIn,
        maxY - cursorY
      );
      if (tableH < 0.15) break;
      slide.addTable(tableRows, {
        ...tableOptions,
        x,
        y: cursorY,
        w,
        h: tableH,
      });
      cursorY += tableH + tableGapIn;
    }
  }
};

export const addReportTextToSlide = (
  slide: PptxGenJS.Slide,
  markdownContent: string,
  { title, ...box }: ReportSlotLayout & { title?: string }
) =>
  addMarkdownSegmentsToSlide(slide, markdownContent, box, {
    layout: 'inline',
    title,
    bodyPt: 10,
    paragraphGapPt: 4,
    tablePt: 9,
    tableRowHIn: 0.3,
    tableGapIn: 0.1,
  });

const resolveReportTableColumnFormat = ({
  columnKey,
  columnMetaMap,
  plotFormat,
}: {
  columnKey: string;
  columnMetaMap: ColumnMetaMap | undefined;
  plotFormat: FormatType | undefined;
}): FormatType | string => {
  const columnMeta = columnMetaMap?.[columnKey];
  if (
    columnMeta?.format !== undefined &&
    columnMeta.format !== null &&
    columnMeta.format !== ''
  ) {
    return columnMeta.format;
  }
  const metricForColumn = columnMeta?.metric;
  if (metricForColumn !== undefined) {
    return getFormat({
      metric: metricForColumn as MetricValues,
      displayAs: 'value',
    });
  }
  if (
    columnMetaMap === undefined &&
    (columnKey === 'VALUE' || columnKey === 'SHARE') &&
    plotFormat !== undefined
  ) {
    return plotFormat;
  }
  return FormatType.SI;
};

const formatReportTableCell = ({
  columnKey,
  value,
  columnMetaMap,
  plotFormat,
  currencyCode,
}: {
  columnKey: string;
  value: unknown;
  columnMetaMap: ColumnMetaMap | undefined;
  plotFormat: FormatType | undefined;
  currencyCode?: string;
}): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const format = resolveReportTableColumnFormat({
    columnKey,
    columnMetaMap,
    plotFormat,
  });
  const formatter = getFormatter(format, { currencyCode });
  const numValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isNaN(numValue) && value !== '') {
    return formatter(numValue);
  }
  return String(value);
};

export const addReportTableToSlide = (
  slide: PptxGenJS.Slide,
  sheetData: Record<string, string | number>[],
  opts: ReportSlotLayout & {
    title?: string;
    columnMetaMap?: ColumnMetaMap;
    tablePlotFormat?: FormatType;
    currencyCode?: string;
  }
) => {
  const { x, y, w, h, title, columnMetaMap, tablePlotFormat, currencyCode } =
    opts;

  const firstRow = sheetData?.[0];
  if (!firstRow) return;
  const columns = Object.keys(firstRow);
  if (columns.length === 0) return;

  const titleTrim = title?.trim() ?? '';
  const headerH = titleTrim ? 0.3 : 0;
  if (titleTrim) {
    slide.addText(titleTrim, {
      x,
      y,
      w,
      h: headerH,
      fontSize: reportPptTheme.fonts.titleSize,
      bold: true,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'left',
    });
  }

  const columnWidth = w / columns.length;
  const colW = columns.map(() => columnWidth);

  const brandFill = toPptxHex(pptTheme.colors.brandPrimary);
  const whiteFill = toPptxHex(pptTheme.colors.white);
  const zebraFill = toPptxHex(pptTheme.colors.grid);
  const textColor = toPptxHex(pptTheme.colors.text);
  const whiteText = toPptxHex(pptTheme.colors.white);

  const headerRow: PptxGenJS.TableRow = columns.map((col) => ({
    text: col,
    options: {
      bold: true,
      fill: { color: brandFill },
      color: whiteText,
      fontSize: 8,
    },
  }));

  const dataRows: PptxGenJS.TableRow[] = sheetData.map((row, rowIndex) => {
    const fillColor = rowIndex % 2 === 0 ? whiteFill : zebraFill;
    return columns.map((col) => ({
      text: formatReportTableCell({
        columnKey: col,
        value: row[col],
        columnMetaMap,
        plotFormat: tablePlotFormat,
        currencyCode,
      }),
      options: { fill: { color: fillColor } },
    }));
  });

  slide.addTable([headerRow, ...dataRows], {
    x,
    y: y + headerH,
    w,
    h: Math.max(0.1, h - headerH),
    colW,
    fontSize: 7,
    border: {
      type: 'solid',
      pt: 0.5,
      color: toPptxHex(pptTheme.colors.grid),
    },
    fontFace: pptTheme.fonts.face,
    color: textColor,
  });
};

export const FOOTER_TEXT = {
  aboutTitle: 'About Revelio Labs data',
  aboutBody:
    'Revelio Labs provides workforce intelligence. We absorb and standardize hundreds of millions of public employment records to create the world\u2019s first universal HR database, allowing us to see current workforce composition of any company. We use the most advanced techniques in data science and statistical modeling to provide clean, reliable workforce data, which we additionally enrich with our proprietary models for greater insight into workforce trends. With our salary prediction model, we are able to estimate salaries for more than 1.4 billion unique positions worldwide, using individual-level information such as job title, seniority level, company, and location.',
  contactTitle: 'Contact',
  contactBody:
    'Thank you for your interest in our data.\n\nBy ingesting the universe of publicly available online profiles, job postings, employee sentiment reviews, and layoff notices, Revelio Labs has created a comprehensive source of workforce data that can be used to analyze companies, occupations, skills, and activities. In addition to attrition, our team of labor economists and data scientists can create customized reports on industry outlooks, talent intelligence, DEI, and more.\n\nFor additional information on the custom insights we can offer, please reach out to info@reveliolabs.com',
  getInTouchTitle: 'Get in touch',
  getInTouchBody:
    'Reach out for additional information on the custom insights we can offer.',
  emailTitle: 'Email us',
  emailBody: 'info@reveliolabs.com',
  visitTitle: 'Visit us',
  visitBody: '860 Broadway, 5th floor, New York, NY 10003',
} as const;

const textColor = toPptxHex(pptTheme.colors.brandPrimary);

type ClosingSlideConfig = NonNullable<
  ReturnType<typeof getActiveBrandConfig>['closingSlide']
>;

/** Natural size of the lockup asset (1922 x 737.33) → height/width ratio. */
const CLOSING_LOCKUP_ASPECT = 737.33 / 1922;

/**
 * Branded closing slide: solid background + centred logo/tagline lockup + a
 * dynamic-year copyright footer. Replaces the default Revelio info slide for
 * brands that set `closingSlide`. Works for both landscape and portrait
 * slide sizes (the caller passes the slide dimensions).
 */
const addBrandedClosingSlide = (
  pptx: PptxGenJS,
  config: ClosingSlideConfig,
  slide: { widthInches: number; heightInches: number }
) => {
  const s = pptx.addSlide();
  s.background = { color: config.backgroundColor.replace('#', '') };

  const logoW = slide.widthInches * 0.3;
  const logoH = logoW * CLOSING_LOCKUP_ASPECT;
  s.addImage({
    path: config.logoImage,
    x: (slide.widthInches - logoW) / 2,
    y: (slide.heightInches - logoH) / 2,
    w: logoW,
    h: logoH,
    sizing: { type: 'contain', w: logoW, h: logoH },
  });

  const year = new Date().getFullYear();
  s.addText(`© ${year}, ${config.copyrightHolder}. All rights reserved.`, {
    x: 0.4,
    y: slide.heightInches - 0.5,
    w: slide.widthInches - 0.8,
    h: 0.3,
    fontFace: pptTheme.fonts.face,
    fontSize: 9,
    color: '595959',
    align: 'left',
    valign: 'middle',
  });
};

export const addReportFooterSlide = (
  pptx: PptxGenJS,
  geometry: {
    widthInches: number;
    heightInches: number;
    marginInches: number;
  } = reportPptTheme.slide
) => {
  const brand = getActiveBrandConfig();
  if (brand.closingSlide) {
    const { widthInches, heightInches } = geometry;
    addBrandedClosingSlide(pptx, brand.closingSlide, {
      widthInches,
      heightInches,
    });
    return;
  }

  const { widthInches, marginInches } = geometry;
  const slide = pptx.addSlide();

  const contentW = widthInches - 2 * marginInches;
  const x = marginInches;
  let cursorY = marginInches;

  const columnGap = 0.12;
  const aboutImageRowH = 2.05;
  const aboutColW = (contentW - columnGap) * (3 / 4);
  const imageColW = (contentW - columnGap) * (1 / 4);

  const CLIENT_LOGOS_VIEWBOX_W = 232;
  const CLIENT_LOGOS_VIEWBOX_H = 257;
  const logoAspect = CLIENT_LOGOS_VIEWBOX_H / CLIENT_LOGOS_VIEWBOX_W;
  const logoMaxW = Math.min(1.35, imageColW - 0.06);
  let logoW = logoMaxW;
  let logoH = logoW * logoAspect;
  const maxLogoH = aboutImageRowH - 0.08;
  if (logoH > maxLogoH) {
    logoH = maxLogoH;
    logoW = logoH / logoAspect;
  }
  const logoX = x + aboutColW + columnGap + (imageColW - logoW) / 2;
  const logoY = cursorY + (aboutImageRowH - logoH) / 2;

  slide.addText(
    [
      {
        text: FOOTER_TEXT.aboutTitle,
        options: { fontSize: 12, bold: true, breakLine: true },
      },
      { text: FOOTER_TEXT.aboutBody, options: { fontSize: 8 } },
    ],
    {
      x,
      y: cursorY,
      w: aboutColW,
      h: aboutImageRowH,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 4,
    }
  );

  slide.addImage({
    path: clientLogos,
    x: logoX,
    y: logoY,
    w: logoW,
    h: logoH,
    sizing: { type: 'contain', w: logoW, h: logoH },
  });

  cursorY += aboutImageRowH + 0.22;

  const contactRowH = 2.35;
  const contactColW = (contentW - columnGap) * (3 / 5);
  const getInTouchColW = (contentW - columnGap) * (2 / 5);

  slide.addText(
    [
      {
        text: FOOTER_TEXT.contactTitle,
        options: { fontSize: 12, bold: true, breakLine: true },
      },
      { text: FOOTER_TEXT.contactBody, options: { fontSize: 8 } },
    ],
    {
      x,
      y: cursorY,
      w: contactColW,
      h: contactRowH,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 4,
    }
  );

  slide.addText(
    [
      {
        text: FOOTER_TEXT.getInTouchTitle,
        options: { fontSize: 10, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.getInTouchBody,
        options: { fontSize: 8, breakLine: true },
      },
      { text: '', options: { fontSize: 5, breakLine: true } },
      {
        text: `${FOOTER_TEXT.emailTitle}: `,
        options: { fontSize: 9, bold: true },
      },
      {
        text: FOOTER_TEXT.emailBody,
        options: { fontSize: 9, breakLine: true },
      },
      {
        text: `${FOOTER_TEXT.visitTitle}: `,
        options: { fontSize: 9, bold: true },
      },
      { text: FOOTER_TEXT.visitBody, options: { fontSize: 9 } },
    ],
    {
      x: x + contactColW + columnGap,
      y: cursorY,
      w: getInTouchColW,
      h: contactRowH,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 3,
    }
  );
};

export const addFooterSlide = (pptx: PptxGenJS) => {
  const brand = getActiveBrandConfig();
  if (brand.closingSlide) {
    addBrandedClosingSlide(pptx, brand.closingSlide, {
      widthInches: 10,
      heightInches: 5.625,
    });
    return;
  }

  const slide = pptx.addSlide();

  slide.addText(
    [
      {
        text: FOOTER_TEXT.aboutTitle,
        options: { fontSize: 14, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.aboutBody,
        options: { fontSize: 9 },
      },
    ],
    {
      x: 0.5,
      y: 0.4,
      w: 6.2,
      h: 2.2,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 6,
    }
  );

  slide.addImage({
    path: clientLogos,
    x: 7.2,
    y: 0.4,
    w: 2.0,
    h: 2.0,
    sizing: { type: 'contain', w: 2.0, h: 2.0 },
  });

  slide.addText(
    [
      {
        text: FOOTER_TEXT.contactTitle,
        options: { fontSize: 14, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.contactBody,
        options: { fontSize: 9 },
      },
    ],
    {
      x: 0.5,
      y: 2.8,
      w: 5.5,
      h: 2.4,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 6,
    }
  );

  slide.addText(
    [
      {
        text: FOOTER_TEXT.getInTouchTitle,
        options: { fontSize: 11, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.getInTouchBody,
        options: { fontSize: 9, breakLine: true },
      },
      { text: '', options: { fontSize: 6, breakLine: true } },
      {
        text: FOOTER_TEXT.emailTitle,
        options: { fontSize: 11, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.emailBody,
        options: { fontSize: 9, breakLine: true },
      },
      { text: '', options: { fontSize: 6, breakLine: true } },
      {
        text: FOOTER_TEXT.visitTitle,
        options: { fontSize: 11, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.visitBody,
        options: { fontSize: 9 },
      },
    ],
    {
      x: 6.5,
      y: 2.8,
      w: 3.0,
      h: 2.4,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 4,
    }
  );
};
